/* Vague 96.7 — Signature setup onboarding modal.
   Triggered from the sidebar banner shown to users without a configured
   email signature. Lets them upload an image OR paste rich HTML; persists
   to /api/user/signature (user_signatures collection). */
import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, PenTool, Loader2, Check } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { sanitizeRichHtml } from '../lib/sanitize';

const DEFAULT_TEMPLATE = `<div style="font-family:Arial,sans-serif;font-size:13px;color:#1A1B2E;line-height:1.5">
  <div style="font-weight:bold">Prénom NOM</div>
  <div style="color:#6B6C85">Fonction · Entreprise</div>
  <div style="color:#4F46E5;margin-top:4px">📞 06 12 34 56 78  ·  📧 contact@exemple.fr</div>
</div>`;

export default function SignatureSetupModal({ open, onClose, onSaved, activeCfgId = null }) {
  const [mode, setMode] = useState('image'); // 'image' | 'html'
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [imageMime, setImageMime] = useState('image/png');
  const [imageWidth, setImageWidth] = useState(420);
  const [html, setHtml] = useState(DEFAULT_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMode('image');
    setImageDataUrl('');
    setImageMime('image/png');
    setImageWidth(420);
    setHtml(DEFAULT_TEMPLATE);
  }, [open]);

  if (!open) return null;

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast({ title: 'Format invalide', description: 'Choisissez une image (PNG, JPG…).', variant: 'destructive' });
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast({ title: 'Image trop volumineuse', description: 'Max 2 Mo.', variant: 'destructive' });
      return;
    }
    const dataUrl = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result || '');
      r.readAsDataURL(f);
    });
    // Validate natural dimensions to avoid stretching a 1x1 image into a slab
    const dimensions = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ w: 0, h: 0 });
      img.src = dataUrl.toString();
    });
    if (dimensions.w < 80 || dimensions.h < 30) {
      toast({
        title: 'Image trop petite',
        description: `Dimensions détectées : ${dimensions.w}×${dimensions.h}px. Minimum recommandé : 80×30px (sinon elle sera étirée et pixellisée).`,
        variant: 'destructive',
      });
      return;
    }
    setImageDataUrl(dataUrl.toString());
    setImageMime(f.type || 'image/png');
    // Auto-cap width slider to image natural width to avoid upscaling
    setImageWidth(Math.min(420, dimensions.w));
  };

  const buildSignatureHtml = () => {
    if (mode === 'image' && imageDataUrl) {
      return `<div style="margin-top:8px"><img src="${imageDataUrl}" alt="Signature" style="width:${imageWidth}px;max-width:100%;height:auto;display:block" /></div>`;
    }
    return html;
  };

  const submit = async () => {
    setSaving(true);
    try {
      // Vague 96.10 — Priority: write to email_configs (Paramètres > Boîte
      // email > Signature email) when an active cfg exists, so the signature
      // is sent by SMTP exactly as configured. Otherwise fall back to
      // /api/user/signature (user_signatures collection).
      if (activeCfgId) {
        const patch = {};
        if (mode === 'image' && imageDataUrl) {
          patch.signature_image_b64 = imageDataUrl;  // backend strips data:URL prefix
          patch.signature_image_mime = imageMime;
          patch.signature_image_width = imageWidth;
        } else if (mode === 'html') {
          patch.signature_html = html.trim();
        } else {
          toast({ title: 'Signature vide', description: 'Importez une image ou saisissez du texte.', variant: 'destructive' });
          setSaving(false);
          return;
        }
        await api.patch(`/email-configs/${activeCfgId}/signature`, patch);
        toast({ title: 'Signature enregistrée ✓', description: 'Configurée dans votre Boîte email — appliquée à tous vos prochains envois.' });
      } else {
        // No SMTP cfg yet: store as user-level signature only
        const sig = (mode === 'image' && imageDataUrl)
          ? `<div style="margin-top:8px"><img src="${imageDataUrl}" alt="Signature" style="max-width:${imageWidth}px;width:auto;height:auto;display:block" /></div>`
          : html.trim();
        if (!sig) {
          toast({ title: 'Signature vide', description: 'Importez une image ou saisissez du texte.', variant: 'destructive' });
          setSaving(false);
          return;
        }
        await api.put('/user/signature', { signature_html: sig });
        toast({ title: 'Signature enregistrée ✓', description: 'Elle sera ajoutée automatiquement à vos prochains emails.' });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde impossible', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const clearSignature = async () => {
    if (!window.confirm('Supprimer définitivement votre signature ?')) return;
    setSaving(true);
    try {
      if (activeCfgId) {
        await api.patch(`/email-configs/${activeCfgId}/signature`, {
          signature_image_b64: '',
          signature_html: '',
          signature_text: '',
        });
      } else {
        await api.put('/user/signature', { signature_html: '' });
      }
      toast({ title: 'Signature supprimée' });
      onSaved?.();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const previewHtml = buildSignatureHtml();

  return (
    <div className="fixed inset-0 z-[150] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="signature-setup-modal" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[760px] max-h-[94vh] overflow-y-auto bg-white rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-[#EFF0F6] flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl brand-gradient flex items-center justify-center">
            <PenTool className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[16px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Configurer ma signature email</h3>
            <p className="text-[12px] text-[#6B6C85]">
              {activeCfgId
                ? <>Sera enregistrée dans <strong>Paramètres &gt; Boîte email &gt; Signature email</strong> — appliquée à tous les envois SMTP.</>
                : 'Ajoutez votre signature pro pour qu\'elle soit attachée automatiquement à tous vos emails CRM.'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#FAFAFE] flex items-center justify-center"><X className="w-4 h-4 text-[#6B6C85]" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Mode tabs */}
          <div className="flex gap-1 p-0.5 bg-[#F6F7FB] rounded-lg w-fit">
            <button data-testid="sig-mode-image" onClick={() => setMode('image')}
              className={`px-3 h-8 rounded-md text-[12.5px] font-semibold transition-colors ${mode === 'image' ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-[#6B6C85]'}`}>
              📸 Image
            </button>
            <button data-testid="sig-mode-html" onClick={() => setMode('html')}
              className={`px-3 h-8 rounded-md text-[12.5px] font-semibold transition-colors ${mode === 'html' ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-[#6B6C85]'}`}>
              ✏️ Texte / HTML
            </button>
          </div>

          {mode === 'image' && (
            <div className="space-y-3">
              {!imageDataUrl ? (
                <button data-testid="sig-upload-btn" onClick={() => fileRef.current?.click()}
                  className="w-full h-32 rounded-xl border-2 border-dashed border-[#D8D9E5] hover:border-[#4F46E5] hover:bg-[#FAFAFE] transition-colors flex flex-col items-center justify-center gap-2 text-[#6B6C85]">
                  <Upload className="w-6 h-6" />
                  <span className="text-[13px] font-semibold">Importer une image de signature</span>
                  <span className="text-[11px]">PNG / JPG · max 2 Mo</span>
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="p-3 bg-[#FAFAFE] rounded-xl border border-[#EFF0F6]">
                    <img data-testid="sig-image-preview" src={imageDataUrl} alt="aperçu signature"
                      style={{ width: `${imageWidth}px`, maxWidth: '100%', height: 'auto', display: 'block' }} />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-[12px] font-semibold text-[#4A4B63] flex items-center gap-2">
                      Largeur :
                      <input data-testid="sig-image-width" type="range" min="200" max="600" step="10"
                        value={imageWidth} onChange={(e) => setImageWidth(Number(e.target.value))}
                        className="accent-[#4F46E5]" />
                      <span className="font-mono text-[11.5px] text-[#4F46E5] w-12">{imageWidth}px</span>
                    </label>
                    <button data-testid="sig-image-replace" onClick={() => fileRef.current?.click()}
                      className="ml-auto h-8 px-2.5 rounded-md text-[11.5px] font-semibold text-[#4F46E5] hover:bg-[#EEF2FF] border border-[#EFF0F6]">
                      Remplacer
                    </button>
                  </div>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="sig-image-input" />
            </div>
          )}

          {mode === 'html' && (
            <div>
              <textarea data-testid="sig-html-input" value={html} onChange={(e) => setHtml(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 rounded-lg border border-[#EFF0F6] text-[12.5px] font-mono resize-y focus:border-[#4F46E5] outline-none" />
              <div className="text-[10.5px] text-[#8E8FA5] mt-1">HTML accepté. Pour un effet pro : utilisez des balises {'<strong>'}, {'<a href>'}, et un {'<div>'} racine avec le style inline.</div>
            </div>
          )}

          {/* Live preview */}
          {previewHtml && previewHtml.trim() && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-1.5">Aperçu</div>
              <div data-testid="sig-preview"
                className="p-3 bg-white border border-dashed border-[#EFF0F6] rounded-lg max-h-[180px] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(previewHtml) }} />
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#EFF0F6] flex items-center justify-end gap-2">
          <button data-testid="sig-clear-btn" onClick={clearSignature} disabled={saving}
            className="h-10 px-3 rounded-lg border border-[#FECACA] text-[12px] font-semibold text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-50 mr-auto">
            Supprimer
          </button>
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-[#EFF0F6] text-[12.5px] font-semibold text-[#4A4B63] hover:bg-[#FAFAFE]">Annuler</button>
          <button data-testid="sig-save-btn" onClick={submit} disabled={saving}
            className="h-10 px-5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
